import type {
    PartnerCockpitContactDetail,
    PartnerCockpitLeadStatus,
    PartnerCockpitOfferRequestContact,
} from '@houzy/schemas/partner-cockpit/offer-requests';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import SendIcon from '@mui/icons-material/Send';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { OfferRequestContactShortcut } from '../../../lib/trackingEvents';
import { FILL_HEIGHT_CARD_SX } from '../fillHeight';
import { ChatStatusAlert } from './OfferRequestChatCard';
import { OfferRequestContactHeader } from './OfferRequestContactCard';

export type OfferRequestChatUnavailableCardProps = {
    contact: PartnerCockpitOfferRequestContact;
    leadStatus: PartnerCockpitLeadStatus;
    contactRestricted: boolean;
    onRevealContactDetail: (detail: PartnerCockpitContactDetail) => void;
    fillHeight?: boolean;
    onContactShortcut?: (kind: OfferRequestContactShortcut) => void;
};

const NOTICE_AREA_MIN_HEIGHT = 420;

// Offer requests forwarded before partner matching have no engagement, so there is nothing to post a message to. The composer is kept so the card matches
// the one on matched leads, but it can never be enabled.
export function OfferRequestChatUnavailableCard({
    contact,
    leadStatus,
    contactRestricted,
    onRevealContactDetail,
    fillHeight = false,
    onContactShortcut,
}: OfferRequestChatUnavailableCardProps): ReactElement {
    const { t } = useTranslation();

    return (
        <Card variant="outlined" sx={fillHeight ? FILL_HEIGHT_CARD_SX : undefined}>
            <OfferRequestContactHeader
                contact={contact}
                leadStatus={leadStatus}
                contactRestricted={contactRestricted}
                onRevealContactDetail={onRevealContactDetail}
                {...(onContactShortcut ? { onContactShortcut } : {})}
            />
            <Divider />
            <CardContent
                sx={{
                    pt: 2,
                    ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
                }}>
                <Stack spacing={2} sx={fillHeight ? { flex: 1, minHeight: 0 } : undefined}>
                    <Stack
                        sx={
                            fillHeight
                                ? { flex: 1, minHeight: 0, justifyContent: 'center' }
                                : { minHeight: NOTICE_AREA_MIN_HEIGHT }
                        }>
                        <ChatStatusAlert severity="warning">{t('OFFER_REQUESTS.CHAT.UNAVAILABLE')}</ChatStatusAlert>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                            disabled
                            placeholder={t('OFFER_REQUESTS.CHAT.INPUT_PLACEHOLDER')}
                            helperText={t('OFFER_REQUESTS.CHAT.UNAVAILABLE_HINT')}
                            multiline
                            maxRows={5}
                            fullWidth
                            size="small"
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                size="small"
                                                disabled
                                                aria-label={t('OFFER_REQUESTS.CHAT.ATTACH_FILE')}>
                                                <AttachFileOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <IconButton color="primary" disabled aria-label={t('OFFER_REQUESTS.CHAT.SEND')}>
                            <SendIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
